import { FC, useState } from "react";
import GenericCrudTable from "@/components/tables/genericCrudTable";
import { getRecordMetadataColumns } from "@/components/tables/recordMetadataColumns";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useTranslation } from "react-i18next";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import Modal from "@/components/modal";
import FileUploadInput from "@/components/forms/inputs/upload";
import GenericNotification from "@/components/notification/genericNotification";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import { apiRequest } from "@/helper/api";
import type { FileUploadRequestOptions } from "@/helper/customHooks/useFileUploadProgress";

const TranslationTable: FC = () => {
  const { t } = useTranslation();
  usePageHeading(t("translations"));

  const [csvUploadModalOpen, setCsvUploadModalOpen] = useState<boolean>(false);
  const [csvUploadInProgress, setCsvUploadInProgress] = useState(false);
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<string>("");
  const [notificationVariant, setNotificationVariant] = useState<
    "success" | "error" | "warning" | "info" | undefined
  >();
  const { errorModal } = useErrorModal();

  const handleCsvExport = () => {
    const href = `/api/v1/translation/export_xlsx/`;

    window.location.href = href;
  };

  const additionalTopToolBarButtons = () => {
    return (
      <>
        <GenericButton onClick={() => setCsvUploadModalOpen(true)}>
          {t("csv_import")}
        </GenericButton>
        <GenericButton onClick={handleCsvExport}>
          {t("csv_export")}
        </GenericButton>
      </>
    );
  };

  const handleCsvUpload = async (
    file: File,
    { onUploadProgress }: FileUploadRequestOptions,
  ) => {
    const formData = new FormData();
    formData.append("file", file);
    await apiRequest({
      entity: "translation",
      additionalRouteParts: { import_xlsx: "/" },
      method: "post",
      submitData: formData,
      shouldNotImpersonate: true,
      disableGlobalSpinner: true,
      config: {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress,
      },
    });

    setNotificationMessage(t("upload_successful"));
    setNotificationVariant("success");
    setShowNotification(true);
  };

  const handleUploadWrapper = async (
    files: File[],
    options: FileUploadRequestOptions,
  ) => {
    if (files.length > 0) {
      await handleCsvUpload(files[0], options);
    }
  };
  const resetStateOnCloseNotification = () => {
    setShowNotification(false);
  };
  return (
    <>
      <GenericCrudTable
        allowColumnFiltering={true}
        showColumnFilterbyDefault={true}
        additionalCustomTopToolbarElements={additionalTopToolBarButtons}
        disableAddItem
        showRowActions={false}
        entity="translation"
        columns={[
          { header: t("id"), accessorKey: "id", id: "id" },
          {
            header: t("language_german"),
            accessorKey: "de",
            id: "de",
            enableForCreate: true,
            textarea: true,
          },
          {
            header: t("language_english"),
            accessorKey: "en",
            id: "en",
            enableForCreate: true,
            textarea: true,
          },
          ...getRecordMetadataColumns(t, [
            "created_on",
            "created_by",
            "modified_on",
            "modified_by",
          ]),
        ]}
      />
      <Modal
        open={csvUploadModalOpen}
        onClose={() => setCsvUploadModalOpen(false)}
        title={t("csv_import")}
        hideDialogActions={true}
        disableCancelButton={csvUploadInProgress}
        maxWidth="40rem"
      >
        <FileUploadInput
          multiple={false}
          onCancel={() => setCsvUploadModalOpen(false)}
          customUploadHandler={handleUploadWrapper}
          accept={[".xlsx"]}
          onUploadStateChange={setCsvUploadInProgress}
          handleUploadSuccess={(modalOpen: boolean) => {
            if (modalOpen) {
              setCsvUploadModalOpen(false);
            }
          }}
        />
      </Modal>
      <GenericNotification
        message={showNotification ? notificationMessage : null}
        variant={notificationVariant}
        handleCloseSnackbar={resetStateOnCloseNotification}
      />
      {errorModal}
    </>
  );
};

export default TranslationTable;
